/**
 * User presence system.
 *
 * Broadcasts the local user's heartbeat via space metadata and consumes
 * remote users' presence data to drive avatar indicators.
 *
 * Metadata keys:  `presence:{userId}` → PresenceData
 *
 * Usage:
 *   import * as presence from '../lib/presence';
 *
 *   presence.start(channel, space, userId, user);
 *   presence.stop();
 *
 *   // Reactive store of remote users
 *   presence.remoteUsers  // Writable<RemoteUser[]>
 */

import { writable } from 'svelte/store';
import type {
	RoolChannel,
	RoolSpace,
	CurrentUser,
	SpaceMember,
} from '@rool-dev/sdk';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Shape stored in space metadata under `presence:{userId}`. */
export interface PresenceData {
	name: string;
	email: string;
	lastSeen: number; // Date.now()
}

export type PresenceStatus = 'active' | 'idle';

/** Derived view of a remote user's presence. */
export interface RemoteUser {
	id: string;
	name: string;
	email: string;
	color: string;
	lastSeen: number;
	status: PresenceStatus;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const METADATA_PREFIX = 'presence:';

/** How often we write a heartbeat (ms). */
const HEARTBEAT_INTERVAL = 60_000;

/** Users seen within this window are "active". */
const ACTIVE_THRESHOLD = 90_000;

/** Users not seen for longer than this are dropped from display. */
const OFFLINE_THRESHOLD = 120_000;

// ---------------------------------------------------------------------------
// Color palette — deterministic from user ID
// ---------------------------------------------------------------------------

const USER_COLORS = [
	'#2e90fa', // blue
	'#f97316', // orange
	'#10b981', // emerald
	'#f43f5e', // rose
	'#8b5cf6', // violet
	'#06b6d4', // cyan
	'#eab308', // yellow
	'#ec4899', // pink
	'#14b8a6', // teal
	'#6366f1', // indigo
	'#84cc16', // lime
	'#ef4444', // red
];

export function colorForUser(userId: string): string {
	let hash = 0;
	for (let i = 0; i < userId.length; i++) {
		hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
	}
	return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const remoteUsers = writable<RemoteUser[]>([]);

/** All members of the current space (fetched on start). */
export const spaceMembers = writable<SpaceMember[]>([]);

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let activeChannel: RoolChannel | null = null;
let localUserId: string | null = null;
let localUser: CurrentUser | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Start broadcasting presence for the local user. Call once after opening a
 * space. Safe to call multiple times — will stop the previous session first.
 *
 * @param ch - The active RoolChannel (for metadata and events)
 * @param space - The RoolSpace admin handle (for listUsers)
 */
export function start(
	ch: RoolChannel,
	space: RoolSpace,
	userId: string,
	user: CurrentUser,
): void {
	stop(); // clean up any prior session

	activeChannel = ch;
	localUserId = userId;
	localUser = user;

	// Seed from existing metadata
	processMetadata(ch.getAllMetadata());

	// Immediately broadcast initial presence
	writePresence();

	// Periodic heartbeat
	heartbeatTimer = setInterval(writePresence, HEARTBEAT_INTERVAL);

	// Listen for metadata changes from other clients
	ch.on('metadataUpdated', handleMetadataUpdated);

	// Clean up on page unload
	window.addEventListener('beforeunload', handleBeforeUnload);

	// Fetch space members (best-effort, non-blocking) — uses RoolSpace admin handle
	space.listUsers().then(
		(members) => spaceMembers.set(members),
		() => spaceMembers.set([]),
	);
}

/** Stop broadcasting. Writes a final "offline" heartbeat. */
export function stop(): void {
	if (heartbeatTimer) {
		clearInterval(heartbeatTimer);
		heartbeatTimer = null;
	}

	// Remove our presence entry so others see us as offline quickly
	if (activeChannel && localUserId) {
		try {
			activeChannel.setMetadata(`${METADATA_PREFIX}${localUserId}`, null);
		} catch {
			// Channel may already be closed — ignore
		}
	}

	if (activeChannel) {
		activeChannel.off('metadataUpdated', handleMetadataUpdated);
	}

	window.removeEventListener('beforeunload', handleBeforeUnload);

	activeChannel = null;
	localUserId = null;
	localUser = null;

	remoteUsers.set([]);
	spaceMembers.set([]);
}

// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------

function writePresence(): void {
	if (!activeChannel || !localUserId || !localUser) return;

	const data: PresenceData = {
		name: localUser.name || localUser.email,
		email: localUser.email,
		lastSeen: Date.now(),
	};

	activeChannel.setMetadata(`${METADATA_PREFIX}${localUserId}`, data);
}

function handleMetadataUpdated(event: {
	metadata: Record<string, unknown>;
	source: string;
}): void {
	// Ignore our own local writes
	if (event.source === 'local_user') return;
	processMetadata(event.metadata);
}

function processMetadata(metadata: Record<string, unknown>): void {
	const now = Date.now();
	const users: RemoteUser[] = [];

	for (const [key, value] of Object.entries(metadata)) {
		if (!key.startsWith(METADATA_PREFIX)) continue;

		const userId = key.slice(METADATA_PREFIX.length);

		// Skip self
		if (userId === localUserId) continue;

		// Skip nulled-out entries (user left)
		if (!value || typeof value !== 'object') continue;

		const data = value as PresenceData;
		if (typeof data.lastSeen !== 'number') continue;

		const age = now - data.lastSeen;

		// Drop users that haven't been seen for too long
		if (age > OFFLINE_THRESHOLD) continue;

		const status: PresenceStatus = age <= ACTIVE_THRESHOLD ? 'active' : 'idle';

		users.push({
			id: userId,
			name: data.name || data.email || userId,
			email: data.email || '',
			color: colorForUser(userId),
			lastSeen: data.lastSeen,
			status,
		});
	}

	// Sort: active users first, then by name
	users.sort((a, b) => {
		if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
		return a.name.localeCompare(b.name);
	});

	remoteUsers.set(users);
}

function handleBeforeUnload(): void {
	// Best-effort final write
	if (activeChannel && localUserId) {
		try {
			activeChannel.setMetadata(`${METADATA_PREFIX}${localUserId}`, null);
		} catch {
			// ignore
		}
	}
}
